import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useI18n } from "@core/i18n/i18n";
import { Exercise } from "@domain/exercise/exercise";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { isNotNil } from "@utils/assert";
import dayjs from "dayjs";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useMemo, useState } from "react";

interface ExerciseCommentTabProps {
	exercise: Exercise;
	editable?: boolean;
	editorName: string;
}

export const ExerciseCommentTab: React.FC<ExerciseCommentTabProps> = props => {
	const { exercise, editable = true, editorName } = props;
	const { format, formatDateFull } = useI18n();
	const [value, setValue] = useState(isNotNil(exercise.comment) ? exercise.comment.text : "");
	const availableTextSize = useMemo(() => APP_CONSTANTS.EXERCISE_MAX_COMMENT_LENGTH - value.length, [value.length]);

	const formattedCommentLastUpdateDate = useMemo(() => {
		if (isNotNil(exercise.comment)) {
			return format("exerciseDetailsModal.lastCommentUpdate", {
				date: `${formatDateFull(dayjs(exercise.comment.lastUpdateDate))} - ${dayjs(exercise.comment.lastUpdateDate).format("HH:mm")}`,
				name: exercise.comment.lastEditorName,
			});
		}
	}, [exercise.comment, format, formatDateFull]);

	return (
		<Content>
			<InputTextarea
				rows={5}
				value={value}
				readOnly={!editable}
				placeholder={format("exerciseDetailsModal.comment.placeholder")}
				onChange={e => {
					setValue(e.target.value);
					if (e.target.value.length === 0) exercise.comment = undefined;
					else
						exercise.comment = {
							text: e.target.value,
							lastUpdateDate: new Date(),
							lastEditorName: editorName,
						};
				}}
				maxLength={APP_CONSTANTS.EXERCISE_MAX_COMMENT_LENGTH}
				autoResize
			/>
			{editable && (
				<InfoComment>
					<InfoText>
						{format("global.dialog.textInput.maxLength", { maxLength: APP_CONSTANTS.EXERCISE_MAX_COMMENT_LENGTH })}
					</InfoText>
					<InfoText style={{ color: availableTextSize == 0 ? colors.rejection : colors.black }}>
						{availableTextSize}
					</InfoText>
				</InfoComment>
			)}
			{isNotNil(exercise.comment) && <LastCommentUpdate>{formattedCommentLastUpdateDate}</LastCommentUpdate>}
		</Content>
	);
};

const Content = styled.div`
	${stack(8, "flexstart", "stretch")};
`;

const InfoComment = styled.div`
	${row(undefined, "spacebetween", "flexend")}
	flex-wrap: wrap;
`;

const InfoText = styled.div`
	color: ${colors.black};
	margin: 8px;
	font-size: 12px;
	font-weight: 600;
	font-family: ${fonts.body};
`;

const LastCommentUpdate = styled.div`
	color: ${colors.primary};
	font-size: 16px;
	font-weight: 400;
	font-family: ${fonts.body};
`;
